import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeftRight,
  BookMarked,
  Clock,
  Flame,
  Gauge,
  GripVertical,
  Home,
  Loader2,
  Percent,
  Plus,
  Route,
  RotateCcw,
  Trash2,
  TreePine,
  LayoutTemplate,
  CloudUpload,
  Repeat,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { clientIanaTimeZone } from '../../utils/clientTimeZone';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import RunningWorkoutSegmentsGraphicEditor from './RunningWorkoutSegmentsGraphicEditor';
import RunningExerciseTemplatePickerModal from './RunningExerciseTemplatePickerModal';
import SegmentMeasureEditorFields from './SegmentMeasureEditorFields';
import { estimatePaceSecPerKm, flipAllDistanceSegmentUnits } from '../../utils/runningSegmentLabels';
import { resolveEffectiveThresholdPaceSecPerKm } from '../../utils/thresholdPaceResolve';
import { computeRunningWorkoutEditorSummary } from '../../utils/runningWorkoutEditorSummary';
import {
  cpPercentToApiNumber,
  nextCpPercentFromInput,
  patchCpPercentWithFollowingRange,
  roundCpPercentStored,
} from '../../utils/runningCpInput';
import {
  collapseSegmentRowsToGroups,
  expandGroupsToSegmentRows,
  hasRepeatGroups,
} from '../../utils/repeatGroupUtils';

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalToIso(localStr) {
  if (!localStr || !String(localStr).trim()) return null;
  const d = new Date(localStr);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const PLANNED_SPORT_KEYS = ['run', 'virtual_run', 'ride', 'swim', 'workout', 'other'];

function strydCollectionSelectLabel(c) {
  const title = String(c?.title || 'Collection').slice(0, 200);
  const n = Number(c?.workoutCount);
  const nn = Number.isFinite(n) ? n : 0;
  return `${title} (${nn})`;
}

/** Local calendar day → unix seconds at local midnight (matches Stryd sandbox scheduling). */
function dateStrToUnixStartLocal(yyyyMmDd) {
  const parts = String(yyyyMmDd || '').split('-').map((x) => parseInt(x, 10));
  if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return NaN;
  const [y, m, d] = parts;
  return Math.floor(new Date(y, m - 1, d, 0, 0, 0, 0).getTime() / 1000);
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex flex-nowrap items-end gap-2 overflow-x-auto rounded-md border border-border bg-card px-2 py-1.5',
        isDragging && 'opacity-70 ring-2 ring-primary/30'
      )}
    >
      <div className="flex shrink-0 flex-col justify-end pb-1">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div className="flex w-max max-w-none flex-nowrap items-end gap-2">{children}</div>
    </div>
  );
}

function SortableGroupRow({ id, cycles, onCyclesChange, onRemove, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border-2 border-primary/40 bg-primary/5 px-2 pb-2 pt-1',
        isDragging && 'opacity-70 ring-2 ring-primary/30'
      )}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Repeat className="h-3.5 w-3.5 text-primary" />
        <div className="flex items-center gap-1 rounded-md border border-primary/30 bg-primary/10 px-1.5">
          <button
            type="button"
            className="text-primary hover:text-primary/70 disabled:opacity-40"
            disabled={cycles <= 1}
            onClick={() => onCyclesChange(Math.max(1, cycles - 1))}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[2.2rem] text-center text-xs font-semibold tabular-nums text-primary">
            {cycles}x
          </span>
          <button
            type="button"
            className="text-primary hover:text-primary/70"
            onClick={() => onCyclesChange(cycles + 1)}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
        </div>
        <span className="text-[10px] text-muted-foreground">repeat group</span>
        <button
          type="button"
          className="ml-auto text-destructive hover:text-destructive/70"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-1.5 pl-5">{children}</div>
    </div>
  );
}

function nextLocalKey() {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function UserRunningExerciseEditorModal({
  open,
  onOpenChange,
  exerciseId,
  /** When set, edit `training_exercises` (library) via `/api/training/exercises/.../running-segments` instead of diary `user_running_exercises`. */
  trainingExerciseId = null,
  /** Diary/planner instance: show the same "Create from template" control as library editing. */
  showTemplateToolbarForInstance = false,
  onSaved,
  trainingLogItemId = null,
  diaryOccurredAt = null,
  diaryMetadata = null,
  /** Pre-loaded segments (skips DB fetch). Used for editing suggestion cards before apply. */
  initialSegments = null,
  /** Title to use when initialSegments is set. */
  initialTitle = null,
  /** When set, shows "Update" instead of "Save" and calls this instead of persisting to DB. Receives { title, segments }. */
  onUpdate = null,
}) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [segments, setSegments] = useState([]);
  const [canReset, setCanReset] = useState(false);
  const [editorViewMode, setEditorViewMode] = useState('graphic');
  const [diaryDateTimeLocal, setDiaryDateTimeLocal] = useState('');
  const [diaryMetaDraft, setDiaryMetaDraft] = useState({});
  const [thresholdPaceSecPerKm, setThresholdPaceSecPerKm] = useState(null);
  const [powerPaceModel, setPowerPaceModel] = useState(null);
  const [distanceUnits, setDistanceUnits] = useState('km');
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryMessage, setLibraryMessage] = useState(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [strydDialogOpen, setStrydDialogOpen] = useState(false);
  const [strydLibs, setStrydLibs] = useState([]);
  const [strydLibsLoading, setStrydLibsLoading] = useState(false);
  /** Stryd publish uses native `<select>` + indices (Radix Select + nested dialogs was unreliable). */
  const [strydLibIdx, setStrydLibIdx] = useState('');
  const [strydCollIdx, setStrydCollIdx] = useState('');
  const [strydPublishBusy, setStrydPublishBusy] = useState(false);
  const [strydScheduleDate, setStrydScheduleDate] = useState('');
  const [strydMsg, setStrydMsg] = useState(null);
  const [graphicShowCpBarLabels, setGraphicShowCpBarLabels] = useState(false);
  const [resolvedCpWatts, setResolvedCpWatts] = useState(null);
  const [recordingMode, setRecordingMode] = useState('outdoor');
  const [groupMode, setGroupMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const mapSegmentRow = (s, idx) => ({
    localKey:
      s.id != null ? `db-${s.id}` : `lib-${idx}-${s.sort_order != null ? s.sort_order : idx}`,
    id: s.id,
    duration_sec: s.duration_sec ?? '',
    distance_m: s.distance_m ?? '',
    split_measure_mode: s.split_measure_mode === 'distance' ? 'distance' : 'duration',
    distance_display_unit:
      s.distance_display_unit === 'miles' || s.distance_display_unit === 'km' ? s.distance_display_unit : null,
    run_intensity_class: s.run_intensity_class || 'work',
    cp_percent: roundCpPercentStored(s.cp_percent ?? ''),
    cp_percent_min: roundCpPercentStored(s.cp_percent_min ?? ''),
    cp_percent_max: roundCpPercentStored(s.cp_percent_max ?? ''),
    repeat_group_id: s.repeat_group_id,
    repeat_lap_index: s.repeat_lap_index,
    slot_in_lap: s.slot_in_lap,
    endless: !!s.endless,
  });

  const load = useCallback(async () => {
    if (!exerciseId && !trainingExerciseId && !initialSegments) return;
    setLoading(true);
    try {
      if (Array.isArray(initialSegments) && initialSegments.length > 0) {
        setTitle(initialTitle || '');
        setDescription('');
        setLocation('');
        setNotes('');
        setCanReset(false);
        setSegments(initialSegments.map((s, idx) => mapSegmentRow(s, idx)));
        const [baselineRes, settingsRes, thresholdsRes] = await Promise.all([
          api.get('/api/training/running-cp-baseline').catch(() => ({ data: {} })),
          api.get('/api/users/settings').catch(() => ({ data: {} })),
          api.get('/api/strava/thresholds').catch(() => ({ data: {} })),
        ]);
        const settingsCp = settingsRes.data?.runningCriticalPowerWatts;
        if (settingsCp != null && Number.isFinite(Number(settingsCp))) {
          setResolvedCpWatts(Number(settingsCp));
        } else if (baselineRes.data?.cpWatts != null) {
          setResolvedCpWatts(Number(baselineRes.data.cpWatts));
        }
        const th = thresholdsRes.data || {};
        const effective = resolveEffectiveThresholdPaceSecPerKm({
          trainingThresholdPaceSecPerKm: th.trainingThresholdPace,
          ceilingThresholdPaceSecPerKm: th.ceilingThresholdPace,
        });
        if (effective != null) {
          setThresholdPaceSecPerKm(effective);
        } else if (th.thresholdPace != null && Number(th.thresholdPace) > 0) {
          setThresholdPaceSecPerKm(Number(th.thresholdPace));
        }
        setDistanceUnits(settingsRes.data?.distanceUnits === 'miles' ? 'miles' : 'km');
        setPowerPaceModel(baselineRes.data?.powerPaceModel ?? null);
      } else if (trainingExerciseId) {
        const [res, baselineRes] = await Promise.all([
          api.get(`/api/training/exercises/${trainingExerciseId}/running-segments`),
          api.get('/api/training/running-cp-baseline').catch(() => ({ data: {} })),
        ]);
        const ex = res.data?.exercise;
        const segs = res.data?.segments || [];
        setTitle(ex?.name || '');
        setDescription(ex?.notes || '');
        setLocation('');
        setNotes('');
        setCanReset(false);
        const tp = res.data?.thresholdPaceSecPerKm;
        setThresholdPaceSecPerKm(
          tp != null && Number.isFinite(Number(tp)) && Number(tp) > 0 ? Number(tp) : null
        );
        setDistanceUnits(res.data?.distanceUnits === 'miles' ? 'miles' : 'km');
        const cpW = res.data?.resolved_cp_watts;
        setResolvedCpWatts(
          cpW != null && Number.isFinite(Number(cpW)) && Number(cpW) > 0 ? Number(cpW) : null
        );
        setPowerPaceModel(res.data?.powerPaceModel ?? baselineRes.data?.powerPaceModel ?? null);
        setSegments(segs.map((s, idx) => mapSegmentRow(s, idx)));
      } else {
        const res = await api.get(`/api/user-running-exercises/${exerciseId}`);
        const ex = res.data?.exercise;
        const segs = res.data?.segments || [];
        setTitle(ex?.title || '');
        setDescription(ex?.description || '');
        setLocation(ex?.location || '');
        setNotes(ex?.notes || '');
        setRecordingMode(ex?.recording_mode || 'outdoor');
        setCanReset(!!ex?.library_training_session_id);
        const tp = res.data?.thresholdPaceSecPerKm;
        setThresholdPaceSecPerKm(
          tp != null && Number.isFinite(Number(tp)) && Number(tp) > 0 ? Number(tp) : null
        );
        setDistanceUnits(res.data?.distanceUnits === 'miles' ? 'miles' : 'km');
        const cpW = res.data?.resolved_cp_watts;
        setResolvedCpWatts(
          cpW != null && Number.isFinite(Number(cpW)) && Number(cpW) > 0 ? Number(cpW) : null
        );
        setPowerPaceModel(res.data?.powerPaceModel ?? null);
        const mappedSegs = segs.map((s, idx) => mapSegmentRow(s, idx));
        setSegments(mappedSegs);
        setGroupMode(hasRepeatGroups(mappedSegs));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [exerciseId, trainingExerciseId, initialSegments, initialTitle]);

  useEffect(() => {
    if (open && (exerciseId || trainingExerciseId || initialSegments)) load();
  }, [open, exerciseId, trainingExerciseId, initialSegments, load]);

  useEffect(() => {
    if (!open) {
      setLibraryMessage(null);
      setResolvedCpWatts(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) setEditorViewMode('graphic');
  }, [open]);

  useEffect(() => {
    if (!open) {
      setDiaryDateTimeLocal('');
      setDiaryMetaDraft({});
      return;
    }
    if (!trainingLogItemId) return;
    setDiaryDateTimeLocal(toDatetimeLocalValue(diaryOccurredAt));
    setDiaryMetaDraft(
      diaryMetadata && typeof diaryMetadata === 'object' && !Array.isArray(diaryMetadata) ? { ...diaryMetadata } : {}
    );
  }, [open, trainingLogItemId, diaryOccurredAt, diaryMetadata]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = segments.findIndex((s) => s.localKey === active.id);
    const newIndex = segments.findIndex((s) => s.localKey === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setSegments((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const payloadSegments = () =>
    segments.map((s, idx) => {
      const sm = s.split_measure_mode === 'distance' ? 'distance' : 'duration';
      return {
      duration_sec: s.duration_sec === '' ? null : parseInt(s.duration_sec, 10) || null,
      distance_m: s.distance_m === '' ? null : parseInt(s.distance_m, 10) || null,
      split_measure_mode: sm,
      distance_display_unit:
        sm === 'distance' && (s.distance_display_unit === 'miles' || s.distance_display_unit === 'km')
          ? s.distance_display_unit
          : null,
      run_intensity_class: s.run_intensity_class || 'work',
      cp_percent: cpPercentToApiNumber(s.cp_percent),
      cp_percent_min: cpPercentToApiNumber(s.cp_percent_min),
      cp_percent_max: cpPercentToApiNumber(s.cp_percent_max),
      repeat_group_id: s.repeat_group_id,
      repeat_lap_index: s.repeat_lap_index,
      slot_in_lap: s.slot_in_lap ?? 0,
      sort_order: idx,
      endless: !!s.endless,
    };
    });

  const saveAll = async () => {
    if (trainingExerciseId) {
      setSaving(true);
      try {
        await api.patch(`/api/training/exercises/${trainingExerciseId}`, {
          name: title.trim() || 'Running workout',
          notes: description.trim() ? description.trim() : null,
        });
        await api.put(`/api/training/exercises/${trainingExerciseId}/running-segments`, {
          segments: payloadSegments(),
        });
        onSaved?.();
        onOpenChange(false);
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!exerciseId) return;
    setSaving(true);
    try {
      const occurredIso = trainingLogItemId ? fromDatetimeLocalToIso(diaryDateTimeLocal) : null;
      await api.patch(`/api/user-running-exercises/${exerciseId}`, {
        title: title || null,
        description: description || null,
        location: location || null,
        notes: notes || null,
        recording_mode: recordingMode || 'outdoor',
        ...(occurredIso ? { scheduled_at: occurredIso, timeZone: clientIanaTimeZone() } : {}),
      });
      await api.put(`/api/user-running-exercises/${exerciseId}/segments`, { segments: payloadSegments() });
      if (trainingLogItemId) {
        const meta = { ...diaryMetaDraft };
        if (!meta.planned_sport) delete meta.planned_sport;
        const logPatch = {
          title: title.trim() ? title.trim().slice(0, 500) : null,
          summary: description.trim() ? description.trim().slice(0, 1000) : null,
          metadata: meta,
        };
        if (occurredIso) {
          logPatch.occurredAt = occurredIso;
          logPatch.timeZone = clientIanaTimeZone();
        }
        await api.patch(`/api/training-log/items/${trainingLogItemId}`, logPatch);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const persistLibraryExercise = async () => {
    if (!trainingExerciseId) return;
    await api.patch(`/api/training/exercises/${trainingExerciseId}`, {
      name: title.trim() || 'Running workout',
      notes: description.trim() ? description.trim() : null,
    });
    await api.put(`/api/training/exercises/${trainingExerciseId}/running-segments`, {
      segments: payloadSegments(),
    });
  };

  const openStrydPublishDialog = async () => {
    setStrydDialogOpen(true);
    setStrydMsg(null);
    setStrydLibIdx('');
    setStrydCollIdx('');
    setStrydScheduleDate('');
    setStrydLibsLoading(true);
    try {
      const cred = await api.get('/api/stryd/credentials');
      if (!cred.data?.connected) {
        setStrydLibs([]);
        setStrydMsg({ type: 'err', text: t('runningExercise.strydNotConnected') });
        return;
      }
      const libRes = await api.get('/api/stryd/workouts/library', {
        params: { include_content: 'true' },
      });
      const libs = Array.isArray(libRes.data?.libraries) ? libRes.data.libraries : [];
      setStrydLibs(libs);
      if (!libs.length) {
        setStrydMsg({ type: 'err', text: t('runningExercise.strydNoLibraries') });
      } else if (libs.length === 1) {
        setStrydLibIdx('0');
      }
    } catch (e) {
      setStrydLibs([]);
      setStrydMsg({
        type: 'err',
        text: e.response?.data?.error || t('runningExercise.strydLibraryLoadError'),
      });
    } finally {
      setStrydLibsLoading(false);
    }
  };

  const publishToStryd = async () => {
    if (!trainingExerciseId) return;
    const libIdxResolved =
      strydLibIdx !== ''
        ? Number(strydLibIdx)
        : strydLibs.length === 1
          ? 0
          : NaN;
    if (
      !Number.isInteger(libIdxResolved) ||
      libIdxResolved < 0 ||
      libIdxResolved >= strydLibs.length ||
      strydCollIdx === ''
    ) {
      setStrydMsg({ type: 'err', text: t('runningExercise.strydPickLibraryCollection') });
      return;
    }
    const libIdx = libIdxResolved;
    const collIdx = Number(strydCollIdx);
    if (!Number.isInteger(collIdx) || collIdx < 0) {
      setStrydMsg({ type: 'err', text: t('runningExercise.strydPickLibraryCollection') });
      return;
    }
    const lib = strydLibs[libIdx];
    const colls = lib?.collections || [];
    const coll = colls[collIdx];
    const libraryKey = lib?.id != null ? String(lib.id).trim() : '';
    const collectionKey = coll?.id != null ? String(coll.id).trim() : '';
    if (!libraryKey || !collectionKey || collIdx >= colls.length) {
      setStrydMsg({ type: 'err', text: t('runningExercise.strydPickLibraryCollection') });
      return;
    }
    setStrydPublishBusy(true);
    setStrydMsg(null);
    try {
      await persistLibraryExercise();
      const payload = {
        training_exercise_id: trainingExerciseId,
        library_key: libraryKey,
        collection_key: collectionKey,
      };
      if (strydScheduleDate && String(strydScheduleDate).trim()) {
        const ts = dateStrToUnixStartLocal(String(strydScheduleDate).trim());
        if (Number.isFinite(ts)) {
          payload.schedule_timestamp = ts;
        }
      }
      if (exerciseId && payload.schedule_timestamp != null) {
        payload.user_running_exercise_id = exerciseId;
      }
      const { data } = await api.post('/api/stryd/workouts/from-training-exercise', payload);
      const wid = data.stryd_workout_id || '';
      if (data.stryd_calendar?.ok === false) {
        setStrydMsg({
          type: 'warn',
          text: t('runningExercise.strydPublishCalendarFailed', {
            id: wid,
            error: data.stryd_calendar.error || t('runningExercise.strydPublishError'),
          }),
        });
      } else if (data.stryd_calendar?.ok) {
        setStrydMsg({
          type: 'ok',
          text: t('runningExercise.strydPublishSuccessScheduled', { id: wid }),
        });
      } else {
        setStrydMsg({
          type: 'ok',
          text: t('runningExercise.strydPublishSuccess', { id: wid }),
        });
      }
      onSaved?.();
    } catch (e) {
      setStrydMsg({
        type: 'err',
        text: e.response?.data?.error || t('runningExercise.strydPublishError'),
      });
    } finally {
      setStrydPublishBusy(false);
    }
  };

  const saveToTrainingLibrary = async () => {
    if (!exerciseId) return;
    setLibraryBusy(true);
    setLibraryMessage(null);
    try {
      await api.post(`/api/user-running-exercises/${exerciseId}/save-as-training-exercise`, {
        segments: payloadSegments(),
        title: title.trim() ? title.trim().slice(0, 500) : null,
      });
      setLibraryMessage({ type: 'ok', text: t('runningExercise.addToLibrarySuccess') });
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.status === 400
          ? t('runningExercise.addToLibraryNoSplits')
          : t('runningExercise.addToLibraryError');
      setLibraryMessage({ type: 'err', text: msg });
    } finally {
      setLibraryBusy(false);
    }
  };

  const resetLibrary = async () => {
    if (!exerciseId) return;
    setSaving(true);
    try {
      await api.post(`/api/user-running-exercises/${exerciseId}/reset-from-library`);
      await load();
      onSaved?.();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const addSegment = () => {
    setSegments((prev) => [
      ...prev,
      {
        localKey: nextLocalKey(),
        duration_sec: 60,
        distance_m: '',
        run_intensity_class: 'work',
        cp_percent: 90,
        cp_percent_min: 88,
        cp_percent_max: 92,
        repeat_group_id: null,
        repeat_lap_index: null,
        slot_in_lap: 0,
        split_measure_mode: 'duration',
        distance_display_unit: null,
      },
    ]);
  };

  const duplicateSegment = (idx) => {
    setSegments((prev) => {
      const row = { ...prev[idx], localKey: nextLocalKey() };
      delete row.id;
      const next = [...prev];
      next.splice(idx + 1, 0, row);
      return next;
    });
  };

  const removeSegment = (idx) => {
    setSegments((prev) => prev.filter((_, i) => i !== idx));
  };

  const userDistDefault = distanceUnits === 'miles' ? 'miles' : 'km';
  const hasDistanceSplits = segments.some((s) => s.split_measure_mode === 'distance');

  const groupedItems = useMemo(() => {
    if (!groupMode) return null;
    return collapseSegmentRowsToGroups(segments);
  }, [groupMode, segments]);

  const groupedSortableKeys = useMemo(() => {
    if (!groupedItems) return [];
    return groupedItems.map((item, idx) =>
      item.type === 'group' ? `grp-${item.groupId}-${idx}` : (item.segment.localKey || `single-${idx}`)
    );
  }, [groupedItems]);

  const handleGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !groupedItems) return;
    const oldIndex = groupedSortableKeys.indexOf(active.id);
    const newIndex = groupedSortableKeys.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove([...groupedItems], oldIndex, newIndex);
    const expanded = expandGroupsToSegmentRows(reordered);
    setSegments(expanded.map((s, idx) => mapSegmentRow(s, idx)));
  };

  const updateGroupCycles = (groupIdx, newCycles) => {
    if (!groupedItems) return;
    const updated = groupedItems.map((item, idx) => {
      if (idx !== groupIdx || item.type !== 'group') return item;
      return { ...item, cycles: Math.max(1, newCycles) };
    });
    const expanded = expandGroupsToSegmentRows(updated);
    setSegments(expanded.map((s, idx) => mapSegmentRow(s, idx)));
  };

  const removeGroup = (groupIdx) => {
    if (!groupedItems) return;
    const updated = groupedItems.filter((_, idx) => idx !== groupIdx);
    const expanded = expandGroupsToSegmentRows(updated);
    setSegments(expanded.map((s, idx) => mapSegmentRow(s, idx)));
  };

  const updateGroupTemplateSegment = (groupIdx, slotIdx, patch) => {
    if (!groupedItems) return;
    const updated = groupedItems.map((item, gIdx) => {
      if (gIdx !== groupIdx || item.type !== 'group') return item;
      const newTemplate = item.template.map((seg, sIdx) =>
        sIdx === slotIdx ? { ...seg, ...patch } : seg
      );
      return { ...item, template: newTemplate };
    });
    const expanded = expandGroupsToSegmentRows(updated);
    setSegments(expanded.map((s, idx) => mapSegmentRow(s, idx)));
  };

  const toggleGroupMode = () => {
    if (groupMode) {
      setSegments((prev) =>
        prev.map((s) => ({
          ...s,
          repeat_group_id: null,
          repeat_lap_index: null,
          slot_in_lap: null,
        }))
      );
    }
    setGroupMode((prev) => !prev);
  };

  const addRepeatGroup = () => {
    const maxGid = segments.reduce(
      (mx, s) => Math.max(mx, s.repeat_group_id != null ? Number(s.repeat_group_id) : 0),
      0
    );
    const gid = maxGid + 1;
    const cycles = 4;
    const newSegs = [];
    for (let lap = 0; lap < cycles; lap++) {
      newSegs.push({
        localKey: nextLocalKey(),
        duration_sec: 60,
        distance_m: '',
        run_intensity_class: 'work',
        cp_percent: 95,
        cp_percent_min: 93,
        cp_percent_max: 97,
        repeat_group_id: gid,
        repeat_lap_index: lap,
        slot_in_lap: 0,
        split_measure_mode: 'duration',
        distance_display_unit: null,
      });
      newSegs.push({
        localKey: nextLocalKey(),
        duration_sec: 60,
        distance_m: '',
        run_intensity_class: 'rest',
        cp_percent: '',
        cp_percent_min: '',
        cp_percent_max: '',
        repeat_group_id: gid,
        repeat_lap_index: lap,
        slot_in_lap: 1,
        split_measure_mode: 'duration',
        distance_display_unit: null,
      });
    }
    setSegments((prev) => [...prev, ...newSegs]);
    setGroupMode(true);
  };

  const paceEstimateOpts = useMemo(
    () => ({ cpWatts: resolvedCpWatts, powerPaceModel }),
    [resolvedCpWatts, powerPaceModel]
  );

  const editorSummary = useMemo(
    () =>
      computeRunningWorkoutEditorSummary(
        segments,
        thresholdPaceSecPerKm,
        distanceUnits,
        t,
        paceEstimateOpts
      ),
    [segments, thresholdPaceSecPerKm, distanceUnits, t, paceEstimateOpts]
  );

  const applyTemplateFromExerciseId = async (sourceExerciseId) => {
    try {
      const res = await api.get(`/api/training/exercises/${sourceExerciseId}/running-segments`);
      const ex = res.data?.exercise;
      const segs = res.data?.segments || [];
      const base = String(ex?.name || '').trim();
      const suffix = t('runningExercise.templateTitleCopySuffix');
      const nextTitle = (base ? `${base}${suffix}` : `${t('runningExercise.newRunningExercise')}${suffix}`).slice(
        0,
        500
      );
      setTitle(nextTitle);
      setDescription((prev) => (prev && String(prev).trim() ? prev : ex?.notes || ''));
      setSegments(
        segs.map((s, idx) => {
          const row = mapSegmentRow(s, idx);
          return { ...row, localKey: nextLocalKey(), id: null };
        })
      );
      setEditorViewMode('graphic');
      setLibraryMessage(null);
      const cpW = res.data?.resolved_cp_watts;
      setResolvedCpWatts(
        cpW != null && Number.isFinite(Number(cpW)) && Number(cpW) > 0 ? Number(cpW) : null
      );
    } catch (e) {
      console.error(e);
      setLibraryMessage({ type: 'err', text: t('runningExercise.templateApplyError') });
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        priority="nested"
        className={cn(
          'flex h-[80vh] max-h-[80vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[95vw]'
        )}
      >
        <div className="shrink-0 border-b border-border px-6 pb-4 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <DialogHeader className="space-y-0 text-left sm:min-w-0 sm:flex-1">
              <DialogTitle>
                {trainingExerciseId ? t('runningExercise.editorTitleLibrary') : t('runningExercise.editorTitle')}
              </DialogTitle>
              <DialogDescription className="sr-only">{t('runningExercise.editorDialogDesc')}</DialogDescription>
              {!!trainingExerciseId && (
                <p className="mt-2 text-xs text-muted-foreground">{t('runningExercise.libraryEditorHint')}</p>
              )}
            </DialogHeader>
            <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
              {!!(trainingExerciseId || showTemplateToolbarForInstance) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-2"
                  disabled={loading}
                  onClick={() => setTemplatePickerOpen(true)}
                >
                  <LayoutTemplate className="h-4 w-4" />
                  {t('runningExercise.createFromTemplate')}
                </Button>
              )}
              {!!trainingExerciseId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-2"
                  disabled={loading || saving}
                  onClick={openStrydPublishDialog}
                >
                  <CloudUpload className="h-4 w-4" />
                  {t('runningExercise.strydPublish')}
                </Button>
              )}
              {!trainingExerciseId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-2"
                  disabled={libraryBusy || !exerciseId || loading}
                  onClick={saveToTrainingLibrary}
                >
                  {libraryBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <BookMarked className="h-4 w-4" />
                  )}
                  {t('runningExercise.addToLibrary')}
                </Button>
              )}
            </div>
          </div>
          {libraryMessage && (
            <p
              className={cn(
                'mt-3 text-xs',
                libraryMessage.type === 'ok' ? 'text-primary' : 'text-destructive'
              )}
              role="status"
            >
              {libraryMessage.text}
            </p>
          )}
        </div>
        {loading ? (
          <div className="flex flex-1 justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <TooltipProvider delayDuration={400}>
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-4 lg:flex-row lg:items-stretch lg:gap-0 lg:overflow-hidden">
              <div className="flex w-full shrink-0 flex-col gap-4 lg:min-h-0 lg:w-[30%] lg:max-w-[30%] lg:flex-none lg:overflow-y-auto lg:pr-6">
                <div className="space-y-1">
                  <Label>{t('runningExercise.fieldTitle')}</Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label>
                    {trainingExerciseId ? t('runningExercise.fieldNotesLibrary') : t('runningExercise.fieldDescription')}
                  </Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="min-h-[5rem] resize-y text-sm"
                  />
                </div>
                {!trainingExerciseId && (
                  <>
                    <div className="space-y-1">
                      <Label>{t('runningExercise.fieldLocation')}</Label>
                      <Input value={location} onChange={(e) => setLocation(e.target.value)} className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('runningExercise.fieldNotes')}</Label>
                      <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('runningExercise.fieldRecordingMode')}</Label>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={recordingMode}
                        onValueChange={(v) => v && setRecordingMode(v)}
                        className="justify-start"
                      >
                        <ToggleGroupItem value="outdoor" className="gap-1.5 text-xs">
                          <TreePine className="h-3.5 w-3.5" />
                          {t('runningExercise.recordingModeOutdoor')}
                        </ToggleGroupItem>
                        <ToggleGroupItem value="treadmill" className="gap-1.5 text-xs">
                          <Gauge className="h-3.5 w-3.5" />
                          {t('runningExercise.recordingModeTreadmill')}
                        </ToggleGroupItem>
                        <ToggleGroupItem value="indoor" className="gap-1.5 text-xs">
                          <Home className="h-3.5 w-3.5" />
                          {t('runningExercise.recordingModeIndoor')}
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  </>
                )}

                {!!trainingLogItemId && (
                  <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{t('trainingLog.diaryScheduleSection')}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{t('trainingLog.diaryScheduleHint')}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('trainingLog.diaryDateTime')}</Label>
                      <Input
                        type="datetime-local"
                        className="h-9 w-full"
                        value={diaryDateTimeLocal}
                        onChange={(e) => setDiaryDateTimeLocal(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('trainingLog.diarySportType')}</Label>
                      <Select
                        value={
                          diaryMetaDraft.planned_sport &&
                          PLANNED_SPORT_KEYS.includes(diaryMetaDraft.planned_sport)
                            ? diaryMetaDraft.planned_sport
                            : 'unset'
                        }
                        onValueChange={(v) => {
                          setDiaryMetaDraft((m) => {
                            const next = { ...m };
                            if (v === 'unset') delete next.planned_sport;
                            else next.planned_sport = v;
                            return next;
                          });
                        }}
                      >
                        <SelectTrigger className="h-9 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unset">{t('trainingLog.diarySportUnset')}</SelectItem>
                          {PLANNED_SPORT_KEYS.map((k) => (
                            <SelectItem key={k} value={k}>
                              {t(`trainingLog.plannedSport.${k}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              <Separator className="lg:hidden" />

              <div className="flex min-h-0 min-w-0 flex-1 basis-0 flex-col gap-4 overflow-hidden border-border lg:min-h-0 lg:border-l lg:pl-6">
                <div className="flex min-h-10 shrink-0 flex-nowrap items-center gap-2 overflow-x-auto py-1">
                  <div className="mr-1 inline-flex shrink-0 rounded-md border border-border bg-muted/40 p-0.5">
                    <Button
                      type="button"
                      size="sm"
                      variant={editorViewMode === 'table' ? 'secondary' : 'ghost'}
                      className="h-8 px-3 text-xs"
                      onClick={() => setEditorViewMode('table')}
                    >
                      {t('runningExercise.editorViewTable')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={editorViewMode === 'graphic' ? 'secondary' : 'ghost'}
                      className="h-8 px-3 text-xs"
                      onClick={() => setEditorViewMode('graphic')}
                    >
                      {t('runningExercise.editorViewGraphic')}
                    </Button>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={addSegment}>
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    {t('runningExercise.addSplit')}
                  </Button>
                  {editorViewMode === 'table' && (
                    <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={addRepeatGroup}>
                      <Repeat className="mr-1 h-3.5 w-3.5" />
                      Add repeat group
                    </Button>
                  )}
                  {editorViewMode === 'table' && hasRepeatGroups(segments) && (
                    <div className="flex shrink-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1">
                      <Label
                        htmlFor="group-mode-toggle"
                        className="cursor-pointer text-[11px] font-normal text-muted-foreground whitespace-nowrap"
                      >
                        <Repeat className="mr-1 inline h-3 w-3" />
                        Group mode
                      </Label>
                      <Switch
                        id="group-mode-toggle"
                        checked={groupMode}
                        onCheckedChange={toggleGroupMode}
                        className="scale-90"
                        aria-label="Toggle repeat group mode"
                      />
                    </div>
                  )}
                  {editorViewMode === 'graphic' && (
                    <div className="flex shrink-0 items-center gap-2 rounded-md border border-border/60 bg-muted/30 px-2 py-1">
                      <Label
                        htmlFor="graphic-cp-labels-toggle"
                        className="cursor-pointer text-[11px] font-normal text-muted-foreground whitespace-nowrap"
                      >
                        {t('runningExercise.graphicShowCpOnBars')}
                      </Label>
                      <Switch
                        id="graphic-cp-labels-toggle"
                        checked={graphicShowCpBarLabels}
                        onCheckedChange={setGraphicShowCpBarLabels}
                        className="scale-90"
                        aria-label={t('runningExercise.graphicShowCpOnBars')}
                      />
                    </div>
                  )}
                  {canReset && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="shrink-0"
                      disabled={saving}
                      onClick={resetLibrary}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" />
                      {t('runningExercise.resetFromLibrary')}
                    </Button>
                  )}
                  <div className="ml-auto flex shrink-0 flex-nowrap items-center gap-3 border-l border-border pl-3 text-xs">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex cursor-default items-center gap-1 whitespace-nowrap">
                          <Route className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="tabular-nums text-foreground">{editorSummary.distanceLabel}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {t('runningExercise.editorSummaryTooltipDistance')}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex cursor-default items-center gap-1 whitespace-nowrap">
                          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="tabular-nums text-foreground">{editorSummary.timeLabel}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {t('runningExercise.editorSummaryTooltipTime')}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex cursor-default items-center gap-1 whitespace-nowrap">
                          <Flame className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="tabular-nums text-foreground">{editorSummary.sufferScoreLabel}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {t('runningExercise.editorSummaryTooltipSuffer')}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex cursor-default items-center gap-1 whitespace-nowrap">
                          <Percent className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="tabular-nums text-foreground">{editorSummary.weightedCpLabel}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {t('runningExercise.editorSummaryTooltipWeightedCp')}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                <div
                  className={cn(
                    'min-h-0 flex-1 basis-0 overflow-x-hidden overscroll-y-contain',
                    editorViewMode === 'graphic'
                      ? 'flex flex-col overflow-hidden'
                      : 'overflow-y-auto'
                  )}
                >
                {editorViewMode === 'graphic' ? (
                  <RunningWorkoutSegmentsGraphicEditor
                    fillContainerHeight
                    segments={segments}
                    setSegments={setSegments}
                    duplicateSegment={duplicateSegment}
                    heightPx={168}
                    thresholdPaceSecPerKm={thresholdPaceSecPerKm}
                    paceEstimateOpts={paceEstimateOpts}
                    distanceUnits={distanceUnits}
                    showBarCpRangeLabels={graphicShowCpBarLabels}
                    resolvedCpWatts={resolvedCpWatts}
                  />
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={groupMode ? handleGroupDragEnd : handleDragEnd}
                  >
                    <SortableContext
                      items={groupMode ? groupedSortableKeys : segments.map((s) => s.localKey)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {groupMode && groupedItems ? (
                          groupedItems.map((item, gIdx) => {
                            const sortKey = groupedSortableKeys[gIdx];
                            if (item.type === 'group') {
                              return (
                                <SortableGroupRow
                                  key={sortKey}
                                  id={sortKey}
                                  cycles={item.cycles}
                                  onCyclesChange={(n) => updateGroupCycles(gIdx, n)}
                                  onRemove={() => removeGroup(gIdx)}
                                >
                                  {item.template.map((ts, slotIdx) => (
                                    <div
                                      key={slotIdx}
                                      className="flex flex-nowrap items-end gap-2 overflow-x-auto rounded-md border border-border bg-card px-2 py-1.5"
                                    >
                                      <div className="flex w-max max-w-none flex-nowrap items-end gap-2">
                                        <SegmentMeasureEditorFields
                                          seg={ts}
                                          onPatch={(partial) => updateGroupTemplateSegment(gIdx, slotIdx, partial)}
                                          distanceUnits={distanceUnits}
                                          thresholdPaceSecPerKm={thresholdPaceSecPerKm}
                                paceEstimateOpts={paceEstimateOpts}
                                  paceEstimateOpts={paceEstimateOpts}
                                          paceEstimateOpts={paceEstimateOpts}
                    paceEstimateOpts={paceEstimateOpts}
                                          t={t}
                                          density="compact"
                                          hideMeasureSelector
                                          layoutVariant="tableRow"
                                        />
                                        <div className="w-[6.5rem] shrink-0 space-y-0.5">
                                          <Label className="text-[10px]">{t('runningExercise.segClass')}</Label>
                                          <Select
                                            value={ts.run_intensity_class || 'work'}
                                            onValueChange={(v) =>
                                              updateGroupTemplateSegment(gIdx, slotIdx, { run_intensity_class: v })
                                            }
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="warmup">warmup</SelectItem>
                                              <SelectItem value="work">work</SelectItem>
                                              <SelectItem value="rest">rest</SelectItem>
                                              <SelectItem value="cooldown">cooldown</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="flex shrink-0 gap-0.5">
                                          <div className="w-11 space-y-0.5">
                                            <Label className="text-[10px]">%CP</Label>
                                            <Input
                                              type="text"
                                              inputMode="numeric"
                                              className="h-8 px-1 text-center text-xs tabular-nums"
                                              value={ts.cp_percent ?? ''}
                                              onChange={(e) => {
                                                const partial = patchCpPercentWithFollowingRange(ts, e.target.value);
                                                if (partial != null) updateGroupTemplateSegment(gIdx, slotIdx, partial);
                                              }}
                                            />
                                          </div>
                                          <div className="w-11 space-y-0.5">
                                            <Label className="text-[10px]">min</Label>
                                            <Input
                                              type="text"
                                              inputMode="numeric"
                                              className="h-8 px-1 text-center text-xs tabular-nums"
                                              value={ts.cp_percent_min ?? ''}
                                              onChange={(e) => {
                                                const next = nextCpPercentFromInput(e.target.value);
                                                if (next !== undefined) updateGroupTemplateSegment(gIdx, slotIdx, { cp_percent_min: next });
                                              }}
                                            />
                                          </div>
                                          <div className="w-11 space-y-0.5">
                                            <Label className="text-[10px]">max</Label>
                                            <Input
                                              type="text"
                                              inputMode="numeric"
                                              className="h-8 px-1 text-center text-xs tabular-nums"
                                              value={ts.cp_percent_max ?? ''}
                                              onChange={(e) => {
                                                const next = nextCpPercentFromInput(e.target.value);
                                                if (next !== undefined) updateGroupTemplateSegment(gIdx, slotIdx, { cp_percent_max: next });
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </SortableGroupRow>
                              );
                            }
                            const s = item.segment;
                            const flatIdx = segments.findIndex((seg) => seg.localKey === s.localKey);
                            return (
                              <SortableRow key={sortKey} id={sortKey}>
                                <SegmentMeasureEditorFields
                                  seg={s}
                                  onPatch={(partial) =>
                                    setSegments((prev) =>
                                      prev.map((r, i) => (i === flatIdx ? { ...r, ...partial } : r))
                                    )
                                  }
                                  distanceUnits={distanceUnits}
                                  thresholdPaceSecPerKm={thresholdPaceSecPerKm}
                                paceEstimateOpts={paceEstimateOpts}
                                  paceEstimateOpts={paceEstimateOpts}
                    paceEstimateOpts={paceEstimateOpts}
                                  t={t}
                                  density="compact"
                                  hideMeasureSelector
                                  layoutVariant="tableRow"
                                />
                                <div className="w-[6.5rem] shrink-0 space-y-0.5">
                                  <Label className="text-[10px]">{t('runningExercise.segClass')}</Label>
                                  <Select
                                    value={s.run_intensity_class}
                                    onValueChange={(v) =>
                                      setSegments((prev) =>
                                        prev.map((r, i) => (i === flatIdx ? { ...r, run_intensity_class: v } : r))
                                      )
                                    }
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="warmup">warmup</SelectItem>
                                      <SelectItem value="work">work</SelectItem>
                                      <SelectItem value="rest">rest</SelectItem>
                                      <SelectItem value="cooldown">cooldown</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex shrink-0 gap-0.5">
                                  <div className="w-11 space-y-0.5">
                                    <Label className="text-[10px]">%CP</Label>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      className="h-8 px-1 text-center text-xs tabular-nums"
                                      value={s.cp_percent}
                                      onChange={(e) => {
                                        const partial = patchCpPercentWithFollowingRange(s, e.target.value);
                                        if (partial == null) return;
                                        setSegments((prev) =>
                                          prev.map((r, i) => (i === flatIdx ? { ...r, ...partial } : r))
                                        );
                                      }}
                                    />
                                  </div>
                                  <div className="w-11 space-y-0.5">
                                    <Label className="text-[10px]">min</Label>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      className="h-8 px-1 text-center text-xs tabular-nums"
                                      value={s.cp_percent_min}
                                      onChange={(e) => {
                                        const next = nextCpPercentFromInput(e.target.value);
                                        if (next === undefined) return;
                                        setSegments((prev) =>
                                          prev.map((r, i) => (i === flatIdx ? { ...r, cp_percent_min: next } : r))
                                        );
                                      }}
                                    />
                                  </div>
                                  <div className="w-11 space-y-0.5">
                                    <Label className="text-[10px]">max</Label>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      className="h-8 px-1 text-center text-xs tabular-nums"
                                      value={s.cp_percent_max}
                                      onChange={(e) => {
                                        const next = nextCpPercentFromInput(e.target.value);
                                        if (next === undefined) return;
                                        setSegments((prev) =>
                                          prev.map((r, i) => (i === flatIdx ? { ...r, cp_percent_max: next } : r))
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="flex shrink-0 gap-0.5">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-destructive"
                                    onClick={() => removeSegment(flatIdx)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </SortableRow>
                            );
                          })
                        ) : (
                          segments.map((s, idx) => (
                            <SortableRow key={s.localKey} id={s.localKey}>
                              <div className="flex shrink-0 items-end gap-0.5">
                                <div className="w-[6.5rem] space-y-0.5">
                                  <Label className="text-[10px]">{t('runningExercise.splitMeasureMode')}</Label>
                                  <Select
                                    value={s.split_measure_mode === 'distance' ? 'distance' : 'duration'}
                                    onValueChange={(v) =>
                                      setSegments((prev) =>
                                        prev.map((r, i) => {
                                          if (i !== idx) return r;
                                          const next = v === 'distance' ? 'distance' : 'duration';
                                          const pace = estimatePaceSecPerKm(
                                            r,
                                            thresholdPaceSecPerKm,
                                            paceEstimateOpts
                                          );
                                          const patch = { split_measure_mode: next };
                                          if (next === 'distance') {
                                            const dur = Number(r.duration_sec) || 0;
                                            if (dur > 0 && pace > 0) {
                                              patch.distance_m = Math.round((dur / pace) * 1000);
                                              patch.duration_sec = 0;
                                            }
                                          } else {
                                            const dm = Number(r.distance_m) || 0;
                                            if (dm > 0 && pace > 0) {
                                              patch.duration_sec = Math.round((dm / 1000) * pace);
                                              patch.distance_m = 0;
                                            }
                                            patch.distance_display_unit = null;
                                          }
                                          return { ...r, ...patch };
                                        })
                                      )
                                    }
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="duration">{t('runningExercise.splitMeasureDuration')}</SelectItem>
                                      <SelectItem value="distance">{t('runningExercise.splitMeasureDistance')}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {s.split_measure_mode === 'distance' && hasDistanceSplits && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 shrink-0"
                                        aria-label={t('runningExercise.flipDistanceUnitsShort')}
                                        onClick={() =>
                                          setSegments((prev) => flipAllDistanceSegmentUnits(prev, userDistDefault))
                                        }
                                      >
                                        <ArrowLeftRight className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs text-xs">
                                      {t('runningExercise.flipDistanceUnitsTooltip')}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <SegmentMeasureEditorFields
                                seg={s}
                                onPatch={(partial) =>
                                  setSegments((prev) =>
                                    prev.map((r, i) => (i === idx ? { ...r, ...partial } : r))
                                  )
                                }
                                distanceUnits={distanceUnits}
                                thresholdPaceSecPerKm={thresholdPaceSecPerKm}
                                paceEstimateOpts={paceEstimateOpts}
                    paceEstimateOpts={paceEstimateOpts}
                                t={t}
                                density="compact"
                                hideMeasureSelector
                                layoutVariant="tableRow"
                              />
                              <div className="w-[6.5rem] shrink-0 space-y-0.5">
                                <Label className="text-[10px]">{t('runningExercise.segClass')}</Label>
                                <Select
                                  value={s.run_intensity_class}
                                  onValueChange={(v) =>
                                    setSegments((prev) =>
                                      prev.map((r, i) => (i === idx ? { ...r, run_intensity_class: v } : r))
                                    )
                                  }
                                >
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="warmup">warmup</SelectItem>
                                    <SelectItem value="work">work</SelectItem>
                                    <SelectItem value="rest">rest</SelectItem>
                                    <SelectItem value="cooldown">cooldown</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex shrink-0 gap-0.5">
                                <div className="w-11 space-y-0.5">
                                  <Label className="text-[10px]">%CP</Label>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    className="h-8 px-1 text-center text-xs tabular-nums"
                                    value={s.cp_percent}
                                    onChange={(e) => {
                                      const partial = patchCpPercentWithFollowingRange(s, e.target.value);
                                      if (partial == null) return;
                                      setSegments((prev) =>
                                        prev.map((r, i) => (i === idx ? { ...r, ...partial } : r))
                                      );
                                    }}
                                  />
                                </div>
                                <div className="w-11 space-y-0.5">
                                  <Label className="text-[10px]">min</Label>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    className="h-8 px-1 text-center text-xs tabular-nums"
                                    value={s.cp_percent_min}
                                    onChange={(e) => {
                                      const next = nextCpPercentFromInput(e.target.value);
                                      if (next === undefined) return;
                                      setSegments((prev) =>
                                        prev.map((r, i) => (i === idx ? { ...r, cp_percent_min: next } : r))
                                      );
                                    }}
                                  />
                                </div>
                                <div className="w-11 space-y-0.5">
                                  <Label className="text-[10px]">max</Label>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    className="h-8 px-1 text-center text-xs tabular-nums"
                                    value={s.cp_percent_max}
                                    onChange={(e) => {
                                      const next = nextCpPercentFromInput(e.target.value);
                                      if (next === undefined) return;
                                      setSegments((prev) =>
                                        prev.map((r, i) => (i === idx ? { ...r, cp_percent_max: next } : r))
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex shrink-0 gap-0.5">
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => duplicateSegment(idx)}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() => removeSegment(idx)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </SortableRow>
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
                </div>
              </div>
            </div>
          </TooltipProvider>
        )}
        <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={saving || loading || (!exerciseId && !trainingExerciseId && !onUpdate)}
            onClick={onUpdate ? () => { onUpdate({ title, segments: payloadSegments() }); onOpenChange(false); } : saveAll}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : onUpdate ? t('common.update') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <RunningExerciseTemplatePickerModal
      open={templatePickerOpen}
      onOpenChange={setTemplatePickerOpen}
      onPickTemplate={applyTemplateFromExerciseId}
    />
    <Dialog open={strydDialogOpen} onOpenChange={setStrydDialogOpen}>
      <DialogContent priority="nested" className="max-w-[min(90vw,480px)]">
        <DialogHeader>
          <DialogTitle>{t('runningExercise.strydPublishTitle')}</DialogTitle>
          <DialogDescription>{t('runningExercise.strydPublishDescription')}</DialogDescription>
        </DialogHeader>
        {strydLibsLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {strydLibs.length > 1 && (
              <div className="space-y-1">
                <Label className="text-xs">{t('runningExercise.strydLibraryLabel')}</Label>
                <select
                  className={cn(
                    'flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm',
                    'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50'
                  )}
                  value={strydLibIdx}
                  onChange={(e) => {
                    setStrydLibIdx(e.target.value);
                    setStrydCollIdx('');
                  }}
                >
                  <option value="">{t('runningExercise.strydLibraryPlaceholder')}</option>
                  {strydLibs.map((lib, i) => (
                    <option key={String(lib.id)} value={String(i)}>
                      {lib.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">{t('runningExercise.strydCollectionLabel')}</Label>
              <select
                className={cn(
                  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm',
                  'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
                value={strydCollIdx}
                disabled={
                  strydLibs.length > 1 ? strydLibIdx === '' : !strydLibs.length
                }
                onChange={(e) => setStrydCollIdx(e.target.value)}
              >
                <option value="">{t('runningExercise.strydCollectionPlaceholder')}</option>
                {(() => {
                  const libIndex =
                    strydLibIdx !== ''
                      ? Number(strydLibIdx)
                      : strydLibs.length === 1
                        ? 0
                        : -1;
                  const cols =
                    libIndex >= 0 ? strydLibs[libIndex]?.collections || [] : [];
                  return cols;
                })().map((c, i) => (
                  <option key={String(c.id)} value={String(i)}>
                    {strydCollectionSelectLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('runningExercise.strydScheduleCalendarLabel')}</Label>
              <Input
                type="date"
                className="h-9"
                value={strydScheduleDate}
                onChange={(e) => setStrydScheduleDate(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                {t('runningExercise.strydScheduleCalendarHint')}
              </p>
            </div>
          </div>
        )}
        {strydMsg && (
          <p
            className={cn(
              'text-xs',
              strydMsg.type === 'ok'
                ? 'text-primary'
                : strydMsg.type === 'warn'
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-destructive'
            )}
            role="status"
          >
            {strydMsg.text}
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => setStrydDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={
              strydPublishBusy ||
              strydLibsLoading ||
              strydCollIdx === '' ||
              (strydLibs.length > 1 && strydLibIdx === '') ||
              !strydLibs.length
            }
            onClick={publishToStryd}
          >
            {strydPublishBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('runningExercise.strydPublishConfirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
